/**
 * Reconciles the desktop QR panel's image list against the upload session on the server.
 *
 * The phone owns the photos: it uploads them and it deletes them, and every change goes through
 * the session the desktop polls. So the server's list for that token is the truth and the desktop
 * is a mirror of it — not a list that only ever grows. The poll used to emit only on a count
 * INCREASE, so a delete on the phone (count decreases) reached no branch and the desktop kept, and
 * then submitted, S3 keys that had been removed (frontend#397).
 */

/** Only the fields the upload session actually owns; anything else is desktop-local. */
interface ServerImageLike {
  s3_key: string;
  extraction_status?: string;
  extracted_products?: unknown[];
  rejection_reason?: string | null;
}

/** Fields the desktop holds that the server never sees, so a resync must carry them over. */
const LOCAL_ONLY_FIELDS = ['localPreview', 'text_input'] as const;

const sameImage = (server: ServerImageLike, local: ServerImageLike | undefined): boolean =>
  !!local &&
  server.s3_key === local.s3_key &&
  server.extraction_status === local.extraction_status &&
  (server.extracted_products?.length ?? 0) === (local.extracted_products?.length ?? 0) &&
  (server.rejection_reason ?? null) === (local.rejection_reason ?? null);

/**
 * True when the session's list differs from what the desktop is holding — by membership, by order,
 * or by any per-image field the server owns. Order counts: the panel renders in list order, so a
 * reorder is a real difference the mirror should adopt.
 *
 * Emitting only on a real difference matters as much as emitting at all: the result flows up to
 * SurveySession.currentResponse, and an emit on every 2s tick would thrash state and re-trigger
 * effects downstream.
 */
export const hasSessionImagesDiverged = (
  serverImages: readonly ServerImageLike[],
  localImages: readonly ServerImageLike[]
): boolean =>
  serverImages.length !== localImages.length ||
  serverImages.some((server, i) => !sameImage(server, localImages[i]));

/**
 * The server's list, with desktop-local fields carried across for images it still holds. Without
 * this, adopting the server list verbatim would erase the per-image `text_input` the respondent
 * typed when extraction failed, and drop the local preview so the tile flashes.
 */
export const mergeSessionImages = <T extends ServerImageLike>(
  serverImages: readonly T[],
  localImages: readonly T[]
): T[] =>
  serverImages.map((server) => {
    const local = localImages.find((img) => img.s3_key === server.s3_key);
    if (!local) return server;
    const preserved: Partial<T> = {};
    for (const field of LOCAL_ONLY_FIELDS) {
      const value = (local as Record<string, unknown>)[field];
      if (value !== undefined) (preserved as Record<string, unknown>)[field] = value;
    }
    return { ...server, ...preserved };
  });
